import { supabase } from './supabase';
import type { CourseTask, TaskSubmission, TaskWithSubmission, TaskResultGroup } from '../types/tasks';

export async function getModuleTask(moduleId: string): Promise<CourseTask | null> {
  const { data, error } = await supabase
    .from('course_tasks')
    .select('*')
    .eq('module_id', moduleId)
    .eq('is_active', true)
    .maybeSingle();
  if (error) { console.error('[tasks] getModuleTask:', error); return null; }
  return data as CourseTask | null;
}

export async function getTaskSubmission(taskId: string, studentId: string): Promise<TaskSubmission | null> {
  const { data, error } = await supabase
    .from('task_submissions')
    .select('*')
    .eq('task_id', taskId)
    .eq('student_id', studentId)
    .maybeSingle();
  if (error) { console.error('[tasks] getTaskSubmission:', error); return null; }
  return data as TaskSubmission | null;
}

export async function submitTask(
  taskId: string,
  studentId: string,
  score: number,
  resultJson: unknown,
): Promise<TaskSubmission> {
  // Leer intento previo para acumular historial
  const existing = await getTaskSubmission(taskId, studentId);
  const prevHistory: number[] = existing?.score_history ?? [];
  const newHistory = [...prevHistory, score];
  const scoreAvg = Math.round(newHistory.reduce((a, b) => a + b, 0) / newHistory.length);

  const { data, error } = await supabase
    .from('task_submissions')
    .upsert(
      {
        task_id: taskId,
        student_id: studentId,
        score,
        score_avg: scoreAvg,
        attempt_count: newHistory.length,
        score_history: newHistory,
        result_json: resultJson,
        submitted_at: new Date().toISOString(),
      },
      { onConflict: 'task_id,student_id' },
    )
    .select()
    .single();
  if (error) throw error;
  return data as TaskSubmission;
}

export async function getStudentTasksForCourse(
  studentId: string,
  courseId: string,
): Promise<TaskWithSubmission[]> {
  // Traer los módulos del curso
  const { data: mods, error: e1 } = await supabase
    .from('modules')
    .select('id, name')
    .eq('course_id', courseId)
    .order('order_index');
  if (e1 || !mods?.length) return [];

  const moduleIds = mods.map((m) => m.id);

  // Tasks activas para esos módulos
  const { data: tasks, error: e2 } = await supabase
    .from('course_tasks')
    .select('*')
    .in('module_id', moduleIds)
    .eq('is_active', true);
  if (e2 || !tasks?.length) return [];

  // Submissions del alumno
  const taskIds = tasks.map((t) => t.id);
  const { data: subs } = await supabase
    .from('task_submissions')
    .select('*')
    .in('task_id', taskIds)
    .eq('student_id', studentId);

  const subMap = new Map((subs ?? []).map((s: TaskSubmission) => [s.task_id, s]));
  const modMap = new Map(mods.map((m: { id: string; name: string }) => [m.id, m.name]));

  return (tasks as CourseTask[]).map((task) => ({
    task,
    submission: subMap.get(task.id) ?? null,
    module_name: modMap.get(task.module_id) ?? '',
  }));
}

export async function getCourseTaskResults(courseId: string): Promise<TaskResultGroup[]> {
  const { data: mods, error: e1 } = await supabase
    .from('modules')
    .select('id, name, order_index')
    .eq('course_id', courseId)
    .order('order_index');
  if (e1 || !mods?.length) return [];

  const moduleIds = mods.map((m: { id: string }) => m.id);

  const { data: tasks, error: e2 } = await supabase
    .from('course_tasks')
    .select('*')
    .in('module_id', moduleIds)
    .eq('is_active', true);
  if (e2 || !tasks?.length) return [];

  const { data: enrollments } = await supabase
    .from('course_enrollments')
    .select('alumno_id')
    .eq('course_id', courseId)
    .neq('status', 'dropped');
  if (!enrollments?.length) return [];

  const alumnoIds: string[] = enrollments.map((e: { alumno_id: string }) => e.alumno_id);

  const { data: profilesData } = await supabase
    .from('profiles')
    .select('id, full_name, email')
    .in('id', alumnoIds);

  const profileMap = new Map(
    (profilesData ?? []).map((p: { id: string; full_name: string | null; email: string }) => [p.id, p]),
  );

  const taskIds = (tasks as CourseTask[]).map((t) => t.id);
  const { data: subs } = await supabase
    .from('task_submissions')
    .select('*')
    .in('task_id', taskIds);

  const subMap = new Map(
    (subs ?? []).map((s: TaskSubmission) => [`${s.task_id}:${s.student_id}`, s]),
  );

  const modMap = new Map(
    mods.map((m: { id: string; name: string; order_index: number }) => [m.id, m]),
  );

  const studentsSorted = [...alumnoIds].sort((a, b) => {
    const na = profileMap.get(a)?.full_name ?? '';
    const nb = profileMap.get(b)?.full_name ?? '';
    return na.localeCompare(nb);
  });

  return (tasks as CourseTask[])
    .sort((a, b) => {
      const oa = modMap.get(a.module_id)?.order_index ?? 0;
      const ob = modMap.get(b.module_id)?.order_index ?? 0;
      return oa - ob;
    })
    .map((task) => {
      const mod = modMap.get(task.module_id)!;
      return {
        module_id: task.module_id,
        module_name: mod.name,
        module_order: mod.order_index,
        task_id: task.id,
        task_title: task.title,
        students: studentsSorted.map((sid) => {
          const p = profileMap.get(sid);
          return {
            student_id: sid,
            student_name: p?.full_name ?? '—',
            student_email: p?.email ?? '—',
            submission: subMap.get(`${task.id}:${sid}`) ?? null,
          };
        }),
      };
    });
}

/** ¿La tarea sigue disponible? null due_date = siempre activa. */
export function isTaskOpen(task: CourseTask): boolean {
  if (!task.due_date) return true;
  return new Date() < new Date(task.due_date);
}
